import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Logo } from './Logo';
import { NetworkBadge } from './NetworkBadge';
import { neighborhoods } from '../data/neighborhoods';
const EASE = [0.23, 1, 0.32, 1] as const;
const HERO_PERSON = 'https://cdn.magicpatterns.com/uploads/6Byp6EPBjhQmnAkAjdn593/FCF4AEFA-3EC8-4D66-A222-6431A919AFEA.png';
export function Hero() {
  const reduce = useReducedMotion();
  const [wordIndex, setWordIndex] = useState(0);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), {
    stiffness: 150,
    damping: 16
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), {
    stiffness: 150,
    damping: 16
  });
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setWordIndex(i => (i + 1) % neighborhoods.length), 2200);
    return () => window.clearInterval(id);
  }, [reduce]);
  const handleMove = (e: React.MouseEvent<HTMLElement>) => {
    if (reduce) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };
  return <section id="top" className="relative overflow-hidden bg-sand" onMouseMove={handleMove} onMouseLeave={handleLeave} data-id="element-129" data-mp="30ggdtgc92mgr">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 pt-12 md:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-24 lg:pt-16" data-id="element-130" data-mp="2qk3kijkmaamj">
        <div className="lg:col-span-7" data-id="element-131" data-mp="2vjzmkd9r7rzg">
          <p className="font-script text-3xl text-flamingo md:text-4xl" data-id="element-132" data-mp="2vglmxcsh5wvp">¡Somos Catrachos!</p>
          <h1 className="mt-2 font-display text-[3.5rem] uppercase leading-[0.92] text-ink sm:text-8xl xl:text-[7.5rem]" data-id="element-133" data-mp="2hl9rt4aeq4e9">
            Rooted in <span className="text-catracho" data-id="element-134" data-mp="s1rtgrkfwcvv">Honduras.</span>
            <br data-id="element-135" data-mp="s44hnmgsjjhd" />
            Raised in
            <br data-id="element-136" data-mp="nat83dklgolr" />
            <span translate="no" className="notranslate relative inline-flex h-[0.95em] overflow-hidden align-bottom" aria-live="polite" data-id="element-137" data-mp="naa12rwhfkmi">
              <AnimatePresence mode="popLayout" initial={false} data-id="element-138" data-mp="ia5imwc0kftn">
                <motion.span key={neighborhoods[wordIndex]} className="block text-sunset" initial={{
                y: '100%',
                opacity: 0
              }} animate={{
                y: '0%',
                opacity: 1
              }} exit={{
                y: '-100%',
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: EASE
              }} data-id="element-139" data-mp="de6nmpw6jzk9">
                  {neighborhoods[wordIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-lg font-medium text-ink/80 md:text-xl" data-id="element-140" data-mp="3lu07neppzt62">
            Celebrating our heritage. Strengthening our community. Creating opportunity across Los Angeles.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3" data-id="element-141" data-mp="1xam4qoj23mxv">
            <a href="#join" className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-4 font-bold text-sand transition-[transform,background-color] duration-150 ease-out hover:bg-catracho active:scale-[0.97]" data-id="element-142" data-mp="21aekbl6vatcq">
              Get Involved
              <ArrowRightIcon size={18} className="transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-143" data-mp="2g0bzxcqqeaqb" />
            </a>
            <a href="#pillars" className="inline-flex items-center rounded-full border-2 border-ink px-7 py-[14px] font-bold text-ink transition-colors duration-150 hover:bg-ink hover:text-sand" data-id="element-144" data-mp="2p0h5dhcd6z1u">
              What We Do
            </a>
            <motion.div className="ml-1 w-24 sm:ml-3 sm:w-32" initial={reduce ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: EASE
          }} data-id="element-145" data-mp="uokf3rpnrz8d">
              <motion.div animate={reduce ? undefined : {
              y: [0, -6, 0]
            }} transition={{
              duration: 4,
              ease: 'easeInOut',
              repeat: Infinity
            }} data-id="element-146" data-mp="3vgvvsob6z945">
                <motion.div style={{
                rotateX,
                rotateY,
                transformPerspective: 900
              }} data-id="element-147" data-mp="9biidk9ynulp">
                  <Logo className="w-full drop-shadow-[0_12px_18px_rgba(14,27,61,0.25)]" data-id="element-148" data-mp="3hphbme5jt1vi" />
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          <div className="mt-10" data-id="element-149" data-mp="3rftvs59gat14">
            <NetworkBadge data-id="element-150" data-mp="3rgvfey4j8nug" />
          </div>
        </div>

        <div className="relative mx-auto flex w-full max-w-sm flex-col self-end sm:max-w-md lg:col-span-5 lg:max-w-none" data-id="element-151" data-mp="3mpn7y7mrjcjx">
          <motion.img src={HERO_PERSON} alt="Young Honduran woman in a Honduras jersey holding a large Honduran flag fan" className="relative z-0 -mb-16 ml-auto block w-full lg:-mb-24" initial={reduce ? false : {
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE,
          delay: 0.05
        }} data-id="element-152" data-mp="2yyzk2cwgccbq" />
        </div>
      </div>

      <div aria-hidden="true" data-id="element-153" data-mp="zm2fjlswbpyf">
        <div className="h-3 bg-sun" data-id="element-154" data-mp="14jkdkh5dkkrn" />
        <div className="h-3 bg-sunset" data-id="element-155" data-mp="14isqjupufvcz" />
        <div className="h-3 bg-flamingo" data-id="element-156" data-mp="14hhpi2ns91pk" />
      </div>
    </section>;
}